import { useEffect } from "react";
import { isNativePlatform } from "../lib/nativeAuth";
import { useUser } from "../users/providers/UserProvider";

/**
 * Owns the native splash screen's dismissal.
 *
 * THE PROBLEM
 * -----------
 * capacitor.config.ts has declared a `SplashScreen` block since the Android
 * wrap was added, but @capacitor/splash-screen was never installed — so the
 * whole block was a dead no-op. Capacitor tore the launch theme down the
 * instant the activity started, which left the user looking at an empty white
 * WebView until the bundle parsed, React mounted and the first route painted.
 *
 * THE FIX
 * -------
 * The plugin is installed and `launchAutoHide` is off, so the splash stays up
 * until the app says so. "The app is ready" is two conditions, not a duration:
 *
 *   1. auth has resolved (`authReady`) — hiding earlier shows the signed-out
 *      shell to a signed-in user for a frame, which is a worse flash than the
 *      white one, and
 *   2. the shell that replaces the splash has actually been painted.
 *
 * A timer cannot express either. It is a guess that is wrong in both
 * directions: too short on a cold start over a slow network, and dead time on
 * every warm start.
 *
 * WHY TWO ANIMATION FRAMES
 * ------------------------
 * React has *committed* the ready tree by the time the effect runs, but the
 * browser has not painted it. The first rAF callback runs before that paint;
 * the second runs after it. Hiding on the second means the splash is removed
 * onto real pixels rather than onto a blank WebView — which is the flash we
 * are trying to remove, just moved later.
 *
 * THE FAILSAFE
 * ------------
 * A splash that never hides is a bricked app — strictly worse than the flash
 * this fixes. So a timer hides it regardless of what auth is doing. `authReady`
 * is set from an async Supabase bridge, and while that bridge already resolves
 * its own failures, "the provider wedged in a way nobody predicted" is exactly
 * the case a failsafe is for. Whichever path gets there first wins;
 * `hideInFlight` and `splashSettled` between them make sure only one of them
 * acts.
 *
 * WHY THE FAILSAFE LIVES OUTSIDE REACT
 * ------------------------------------
 * It used to be a `setTimeout` in a mount effect whose cleanup cleared it. That
 * cancelled the failsafe in precisely the situation it exists for. App.js wraps
 * everything — including SplashGate — in <ErrorBoundary>, and a boundary that
 * catches renders its fallback *instead of* its children. So any render error
 * unmounted SplashGate, the cleanup cleared the timer, and `hide()` was never
 * called. With `launchAutoHide: false` the native splash then stays up forever,
 * swallowing touches, so the error screen underneath is invisible AND
 * unreachable — its "Go home" button is behind the splash.
 *
 * Worse, an error thrown during the *initial* render means SplashGate never
 * mounts at all, so a mount effect would never have armed anything.
 *
 * Hence: module-level timer, armed from index.js at startup rather than from a
 * component, and never cancelled by an unmount. Only a confirmed hide clears it
 * — see hideNativeSplash for why "we asked" is not good enough.
 *
 * A no-op on web: @capacitor/splash-screen is imported lazily and only on
 * native, so neither the browser bundle nor Jest ever loads the native
 * package. Mirrors useAuthDeepLink (#83).
 */

/** Upper bound on how long a user can be left staring at the splash. */
export const SPLASH_FAILSAFE_MS = 4000;

/**
 * How long to wait for one `SplashScreen.hide()` before calling it failed.
 *
 * `await` on a promise that never settles is not an error anyone can catch —
 * the catch below is simply never reached, and neither is the retry that lives
 * in it. A bridge call that goes out and never comes back is a real Capacitor
 * failure mode (the activity was recreated underneath it, the call was
 * dropped), and from in here it is indistinguishable from one that is still
 * working. So the await has to be able to fail on its own clock.
 *
 * This is not belt-and-braces on top of the reordering below — the two only
 * work together. Move the guard without adding this and a hanging hide() is
 * STILL terminal: the failsafe comes due, calls hideNativeSplash(), meets the
 * `hideInFlight` guard and is handed back the very promise that is hanging. One
 * attempt, forever. Measured: with the reorder alone, a hide() that never
 * settles produces exactly 1 attempt; with this, 3.
 */
export const SPLASH_HIDE_TIMEOUT_MS = 2000;

/**
 * How many times to ask before giving up for good.
 *
 * Bounded, not unconditional. Re-arming on every failure turns a plugin that is
 * permanently broken into a 4-second retry loop for the life of the process,
 * spending battery and log space on a call that has already answered. Three
 * attempts covers what this exists for — a transient failure while the activity
 * is being restored — and then stops.
 */
export const SPLASH_HIDE_MAX_ATTEMPTS = 3;

/**
 * Module scope, not component state: these have to outlive any component,
 * because the failure this guards against is a component being torn down. See
 * "WHY THE FAILSAFE LIVES OUTSIDE REACT" above.
 */
let splashSettled = false;
let failsafeTimer = null;

/**
 * The attempt currently in flight, if any, and how many we have started.
 *
 * `hideInFlight` is what keeps "the first caller wins" true now that
 * `splashSettled` is no longer set on entry. The ready path, the failsafe and
 * the error boundary all call hideNativeSplash() without coordinating; without
 * this, a second caller arriving mid-attempt would run its own dynamic import
 * and its own hide().
 */
let hideInFlight = null;
let hideAttempts = 0;

/**
 * Settle `promise`, but reject it ourselves if it has not answered within
 * SPLASH_HIDE_TIMEOUT_MS. See that constant for why an await that cannot time
 * out is a dead end.
 */
const withHideTimeout = (promise) => {
  let timer = null;
  const expiry = new Promise((_, reject) => {
    timer = setTimeout(() => {
      reject(
        new Error(
          `SplashScreen.hide() did not answer in ${SPLASH_HIDE_TIMEOUT_MS}ms`
        )
      );
    }, SPLASH_HIDE_TIMEOUT_MS);
  });

  // Promise.race subscribes to both inputs, so whichever one loses cannot
  // resurface as an unhandled rejection.
  return Promise.race([promise, expiry]).finally(() => {
    if (timer) clearTimeout(timer);
  });
};

/** The splash is dealt with, one way or another: stand the failsafe down. */
const settleSplash = () => {
  splashSettled = true;
  if (failsafeTimer) {
    clearTimeout(failsafeTimer);
    failsafeTimer = null;
  }
};

const runHideAttempt = async () => {
  hideAttempts += 1;

  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await withHideTimeout(SplashScreen.hide());
    // Here, and nowhere earlier — see hideNativeSplash's docblock.
    settleSplash();
    return true;
  } catch (err) {
    // The splash is a cosmetic overlay; if hiding it throws, the app behind it
    // is still fine. Deciding whether to try again is the caller's job, because
    // it is the caller that owns the in-flight slot a retry needs free.
    console.warn("[splash] hide failed:", err);
    return false;
  }
};

/**
 * Ask the native splash to go away. Resolves to whether it actually did, and
 * never rejects — a failure here must not escape into the app.
 *
 * Idempotent: the first caller wins, and a later one either joins the attempt
 * already running (and gets its result) or is a no-op once the splash is down
 * or we have given up. That is what lets the ready path, the failsafe and the
 * error boundary all call it without coordinating.
 *
 * WHY NOTHING IS STOOD DOWN UNTIL THE HIDE CONFIRMS
 * -------------------------------------------------
 * This used to set `splashSettled` and clear the failsafe on entry, BEFORE
 * awaiting hide(). That made a single failure terminal: the one-shot flag was
 * already spent so armSplashFailsafe() refused to re-arm, the only pending
 * timer had already been cleared, and the catch merely logged. With
 * `launchAutoHide: false` and no `launchShowDuration` there is nothing behind
 * us — no OS timeout, no Capacitor timeout — so the native splash stayed up for
 * the life of the process, swallowing every touch on an app that was otherwise
 * working perfectly. Standing the recovery path down optimistically is only
 * safe when the thing it recovers from cannot happen.
 *
 * So both now happen only after a confirmed success, and a failure re-arms the
 * failsafe for another go, bounded by SPLASH_HIDE_MAX_ATTEMPTS.
 *
 * On the success path — the only one a working device ever takes — the
 * observable behaviour is unchanged: one hide(), guard set, timer cleared,
 * `true`.
 */
export const hideNativeSplash = async () => {
  if (!isNativePlatform()) return false;
  if (splashSettled) return false;
  if (hideInFlight) return hideInFlight;

  hideInFlight = runHideAttempt();

  try {
    return await hideInFlight;
  } finally {
    hideInFlight = null;

    // The retry policy lives here rather than in runHideAttempt's catch because
    // this is the frame that owns the in-flight slot, and a retry cannot start
    // a fresh attempt until that slot is free — it is freed on the line above.
    if (!splashSettled) {
      if (hideAttempts >= SPLASH_HIDE_MAX_ATTEMPTS) {
        // Out of attempts. Settle so the failsafe stops re-arming: the splash
        // is stuck whatever we do now, and a retry loop that cannot win is pure
        // cost. The user is left with the same frozen app as before this fix —
        // but only after three genuine tries, rather than after one.
        settleSplash();
      } else {
        armSplashFailsafe();
      }
    }
  }
};

/**
 * Start the clock on the splash. Call once at startup, from module scope —
 * NOT from a component, whose mount is exactly what a render error prevents.
 * Idempotent, and deliberately has no matching "disarm": only a confirmed hide
 * (or running out of attempts) stops it.
 */
export const armSplashFailsafe = () => {
  if (!isNativePlatform() || splashSettled || failsafeTimer) return;

  failsafeTimer = setTimeout(() => {
    failsafeTimer = null;
    hideNativeSplash();
  }, SPLASH_FAILSAFE_MS);
};

/**
 * Test-only. Module-level state persists between tests in a file, so each one
 * has to start from a cold app.
 */
export const __resetSplashForTests = () => {
  if (failsafeTimer) clearTimeout(failsafeTimer);
  failsafeTimer = null;
  splashSettled = false;
  hideInFlight = null;
  hideAttempts = 0;
};

export const useHideSplashWhenReady = (ready) => {
  // Belt and braces for anything that mounts the gate without going through
  // index.js (tests, Storybook). Arming is idempotent, and note there is no
  // cleanup — an unmount must NOT cancel the failsafe.
  useEffect(() => {
    armSplashFailsafe();
  }, []);

  // The intended path: auth resolved, then one painted frame.
  useEffect(() => {
    if (!isNativePlatform()) return undefined;
    if (!ready || splashSettled) return undefined;

    let innerFrame = 0;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        hideNativeSplash();
      });
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [ready]);
};

/**
 * Renders nothing; exists only to read `authReady` from inside UserProvider.
 * App.js renders the provider itself and so cannot call useUser directly.
 */
const SplashGate = () => {
  const { authReady } = useUser();
  useHideSplashWhenReady(authReady);
  return null;
};

export default SplashGate;
